/**
 * A porta entre a tela de compras e a API do módulo 09.
 */
import { pedir } from '@/lib/pedido';

/** As quatro origens da necessidade, na grafia do banco (ferramenta 1 do v6). */
export type OrigemDaRequisicao = 'revisao' | 'ordem_servico' | 'minimo' | 'consumo' | 'manual';

export type SituacaoDoPedido =
  | 'rascunho'
  | 'aguardando aprovacao'
  | 'aprovado'
  | 'emitido'
  | 'recebido'
  | 'cancelado';

export interface ItemDaNecessidade {
  readonly produtoId: string;
  readonly codigo: string;
  readonly produto: string;
  readonly unidade: string;
  readonly critico: boolean;
  readonly demanda: number;
  readonly disponivel: number;
  /** O que já foi pedido e ainda não chegou. É a parcela nova do módulo 09. */
  readonly emPedido: number;
  /** `demanda − disponivel − emPedido`, nunca negativo. A conta vem do banco. */
  readonly comprar: number;
  readonly precisaAte: string | null;
  readonly menorPrazoDias: number | null;
  readonly daPreventiva: boolean;
  readonly doMinimo: boolean;
  /**
   * A previsão de consumo (0091): saídas fora de O.S. preventiva nos últimos
   * `diasDaMedia` dias ÷ `diasDaMedia` × o prazo do fornecedor. Ausentes em
   * servidor anterior; nulos quando o item não tem previsão.
   */
  readonly doConsumo?: boolean;
  readonly demandaDoConsumo?: number | null;
  readonly consumoDiario?: number | null;
  readonly prazoDaPrevisao?: number | null;
  readonly diasDaMedia?: number;
  /**
   * R06 (p.71): a demanda da preventiva vem de alguma máquina sem aplicação
   * validada do item. Ausente em servidor anterior.
   */
  readonly semAplicacaoValidada?: boolean;
  /**
   * 0115 · a necessidade registrada à mão (Registrar necessidade), aberta e
   * ainda não recebida, soma na demanda. Ausentes em servidor anterior.
   */
  readonly daRequisicao?: boolean;
  readonly demandaDaRequisicao?: number | null;
}

export interface Requisicao {
  readonly id: string;
  readonly produtoId: string;
  readonly codigo: string;
  readonly produto: string;
  readonly unidade: string;
  readonly quantidade: number;
  readonly origem: OrigemDaRequisicao;
  readonly ordemId: string | null;
  readonly ordem: number | null;
  readonly justificativa: string;
  readonly situacao: 'aberta' | 'atendida' | 'cancelada';
  readonly precisaAte: string | null;
  readonly criadoEm: string;
}

export interface Pedido {
  readonly id: string;
  readonly numero: string;
  readonly fornecedor: string;
  readonly situacao: SituacaoDoPedido;
  readonly prazoDias: number | null;
  readonly aprovadoEm: string | null;
  readonly emitidoEm: string | null;
  readonly itens: number;
  readonly valorEstimado: number;
  /** Quanto ainda falta chegar, somado. Zero quer dizer pedido atendido. */
  readonly faltaChegar: number;
  readonly ultimoRecebimentoEm: string | null;
}

export interface ItemDoPedido {
  readonly itemId: string;
  readonly produtoId: string;
  readonly codigo: string;
  readonly produto: string;
  readonly unidade: string;
  readonly pedida: number;
  readonly recebida: number;
  readonly falta: number;
  readonly ultimoRecebimentoEm: string | null;
  /** Ausentes em servidor anterior: o valor da linha que quem aprova le (M23). */
  readonly precoUnitario?: number | null;
  readonly valorEstimado?: number | null;
  /** 0088 · as embalagens do item (CX 12): o recebimento pode ser nelas, e o banco converte. */
  readonly conversoes?: readonly { readonly id: string; readonly unidade: string; readonly fator: number }[];
}

/** O cabecalho do pedido aberto sozinho (M23). */
export interface CabecalhoDoPedido {
  readonly id: string;
  readonly numero: string;
  readonly situacao: SituacaoDoPedido;
  readonly fornecedor: string;
  readonly prazoDias: number | null;
  readonly criadoEm: string;
  readonly aprovadoEm: string | null;
  readonly aprovadoPor: string | null;
  readonly emitidoEm: string | null;
  /** O último "Pedir ajuste" (0079): o motivo, quem pediu e quando. */
  readonly motivo?: string | null;
  readonly por?: string | null;
  readonly em?: string | null;
  readonly enviadoEm?: string | null;
  readonly enviadoPor?: string | null;
}

/** A alçada de compra de quem está vendo o pedido (0079). Sem `regra`, vale só a licença. */
export interface AlcadaDoPedido {
  readonly regra: boolean;
  readonly semTeto: boolean;
  readonly limite: number | null;
  readonly valor: number | null;
  readonly alcanca: boolean;
}

export const api = {
  /** A conta vem escrita junto do número — ver o comentário na rota. */
  necessidade: () =>
    pedir<{ itens: readonly ItemDaNecessidade[]; conta: string; premissaDoConsumo?: string }>('/compras/necessidade'),
  requisicoes: () => pedir<{ requisicoes: readonly Requisicao[] }>('/compras/requisicoes'),
  pedidos: () => pedir<{ pedidos: readonly Pedido[] }>('/compras/pedidos'),
  itensDoPedido: (id: string) =>
    pedir<{ pedido?: CabecalhoDoPedido; itens: readonly ItemDoPedido[]; alcada?: AlcadaDoPedido }>(`/compras/pedidos/${encodeURIComponent(id)}`),

  criarRequisicao: (corpo: {
    readonly produtoId: string;
    readonly quantidade: number;
    readonly origem: OrigemDaRequisicao;
    readonly ordemId?: string | null;
    readonly justificativa?: string;
    readonly precisaAte?: string | null;
  }) => pedir<{ id: string }>('/compras/requisicoes', { method: 'POST', body: JSON.stringify(corpo) }),

  criarPedido: (corpo: { readonly numero: string; readonly fornecedorId: string; readonly prazoDias?: number | null }) =>
    pedir<{ id: string; situacao: SituacaoDoPedido }>('/compras/pedidos', {
      method: 'POST',
      body: JSON.stringify(corpo),
    }),
  acrescentarItem: (pedidoId: string, corpo: {
    readonly produtoId: string;
    readonly depositoId: string;
    readonly quantidade: number;
    readonly precoUnitario?: number | null;
    readonly requisicaoId?: string | null;
  }) => pedir<{ id: string }>(`/compras/pedidos/${encodeURIComponent(pedidoId)}/itens`, {
    method: 'POST',
    body: JSON.stringify(corpo),
  }),
  aprovar: (id: string) =>
    pedir<{ id: string }>(`/compras/pedidos/${encodeURIComponent(id)}/aprovacao`, { method: 'POST' }),
  /** Enviar a quem tem alçada maior (0079): o sino avisa. */
  enviar: (id: string) => pedir<{ id: string }>(`/compras/pedidos/${encodeURIComponent(id)}/envio`, { method: 'POST' }),
  /** Devolver ao rascunho com o que mudar (0079). */
  pedirAjuste: (id: string, motivo: string) =>
    pedir<{ id: string }>(`/compras/pedidos/${encodeURIComponent(id)}/ajuste`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ motivo }),
    }),
  emitir: (id: string) =>
    pedir<{ id: string }>(`/compras/pedidos/${encodeURIComponent(id)}/emissao`, { method: 'POST' }),
  cancelar: (id: string, motivo: string) =>
    pedir<{ id: string }>(`/compras/pedidos/${encodeURIComponent(id)}/cancelamento`, {
      method: 'POST',
      body: JSON.stringify({ motivo }),
    }),
  receber: (itemId: string, corpo: {
    readonly quantidade: number;
    /** 0088 · com ela, quantidade e custo são na embalagem. */
    readonly conversaoId?: string;
    readonly recebidoEm?: string;
    readonly documento?: string;
    readonly custoUnitario?: number | null;
    readonly observacao?: string;
  }) => pedir<{ id: string; movimentoId: string; pedidoCompleto: boolean; quantidade?: number | null }>(
    `/compras/itens/${encodeURIComponent(itemId)}/recebimentos`,
    { method: 'POST', body: JSON.stringify(corpo) },
  ),
};
